import React, { useEffect, useState } from "react";
import { Box, Button, Container, Flex, HStack, Image, Skeleton, VStack, Text, useToast } from "@chakra-ui/react";
import "./ScrollIndicator.css";
import MetaTags from "../../../layouts/HelmetTags";

function RefundVerification(): React.JSX.Element {
  const toast = useToast();
  const [isFirstImageLoaded, setIsFirstImageLoaded] = useState(false);
  const [isSecondImageLoaded, setIsSecondImageLoaded] = useState(false);
  const [isThirdImageLoaded, setIsThirdImageLoaded] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);

  const showSuccessToast = () => {
    toast({
      position: "top",
      duration: 2000,
      isClosable: true,
      render: () => (
        <Box
          width="227px"
          height="28px"
          m="auto auto"
          bg="rgba(0, 0, 0, 0.50)"
          color="#A9FFF9"
          borderRadius="10px"
          fontSize="11px"
          fontStyle="normal"
          fontWeight="400"
          lineHeight="18px"
          letterSpacing="-0.22px"
          textAlign="center"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          복사되었습니다
        </Box>
      ),
    });
  };

  // 첫번째 이미지
  useEffect(() => {
    const firstImage: HTMLImageElement = new window.Image();
    firstImage.src = "/events/refund-verification/refund-verification-1.png";
    firstImage.onload = () => setIsFirstImageLoaded(true);
  }, []);

  // 첫 번째 이미지 로드후 두 번째 이미지 프리로딩
  useEffect(() => {
    if (isFirstImageLoaded) {
      const secondImage: HTMLImageElement = new window.Image();
      secondImage.src = "/events/refund-verification/refund-verification-2.png?version=1";
      secondImage.onload = () => setIsSecondImageLoaded(true);
    }
  }, [isFirstImageLoaded]);

  useEffect(() => {
    if (isSecondImageLoaded) {
      const thirdImage: HTMLImageElement = new window.Image();
      thirdImage.src = "/events/refund-verification/refund-verification-3.png?version=1";
      thirdImage.onload = () => setIsThirdImageLoaded(true);
      const finalImage: HTMLImageElement = new window.Image();
      finalImage.src = "/events/refund-verification/refund-verification-4.png?version=1";
      finalImage.onload = () => setIsThirdImageLoaded(true);
    }
  }, [isThirdImageLoaded]);

  // 스크롤 위치 체크
  useEffect(() => {
    const handleScroll = () => {
      // 현재 스크롤 위치
      const currentScroll = window.scrollY + window.innerHeight;
      // 전체 높이
      const totalHeight = document.documentElement.scrollHeight;
      // 하단에서 100px 정도 위치에 도달하면 화살표 숨기기
      setShowScrollIndicator(currentScroll < totalHeight - 100);
    };
    window.addEventListener("scroll", handleScroll);
    // 초기 스크롤 상태 체크
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <MetaTags
        title="덧셈 환급 인증 이벤트"
        description="이 링크로 접속하면 2천원 지급!"
        image="https://plus-603842133488.asia-northeast3.run.app/events/refund-verification/refund-verification-meta.png"
        url="https://plus-603842133488.asia-northeast3.run.app/refund-verification/"
      />
      <Box
        w="100%"
        minH="100vh"
        bg="white"
        display="flex"
        justifyContent="center"
      >
        <Container
          maxW="450px"
          p={0}
          pt="env(safe-area-inset-top)" // iOS 노치 영역만큼 상단 패딩 추가
          display="flex"
          flexDirection="column"
          width="100%"
          alignItems="center"
        >
          <VStack width="100%" align="stretch" alignItems="center" spacing={0}>
            {!isFirstImageLoaded ? (
              <Skeleton
                roundedBottom="10px"
                width="100%"
                height="600px"
              />
            ) : (
              <Image
                src="/events/refund-verification/refund-verification-1.png"
                alt="First content"
                width="100%"
                height="auto"
                loading="eager"
                objectFit="cover"
              />
            )}
            {!isSecondImageLoaded ? (
              <Skeleton
                roundedTop="10px"
                width="100%"
                height="200px"
              />
            ) : (
              <Image
                src="/events/refund-verification/refund-verification-2.png"
                alt="Second content"
                width="100%"
                height="auto"
                loading="eager"
                objectFit="cover"
              />
            )}
            {!isSecondImageLoaded ? (
              <Skeleton
                roundedTop="10px"
                width="100%"
                height="200px"
              />
            ) : (
              <>
                <Image
                  src="/events/refund-verification/refund-verification-3.png"
                  alt="Third content"
                  width="100%"
                  height="auto"
                  loading="eager"
                  objectFit="cover"
                />
                <Box display="flex" justifyContent="center" alignItems="center" backgroundColor="#F6F7F8" w="100%">
                  <Button
                    h="29px"
                    w="191px"
                    color="#777777"
                    borderRadius="10px"
                    backgroundColor="white"
                    leftIcon={
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="12" viewBox="0 0 13 12" fill="none">
                        <g clipPath="url(#clip0_22718_52857)">
                          <path
                            d="M5.13148 11.184L7.70888 8.79549C8.27616 8.26739 8.5896 7.56885 8.5896 6.8247C8.5896 6.08055 8.27616 5.38201 7.70888 4.8539C7.42135 4.58745 7.0846 4.38581 6.71159 4.24658L6.53804 4.40741C6.31786 4.61145 6.1987 4.88271 6.1987 5.17317C6.1987 5.24278 6.20647 5.31239 6.21943 5.37961C6.42665 5.45882 6.61834 5.57165 6.77635 5.72048C7.4343 6.3278 7.4343 7.3216 6.77635 7.92892L4.19636 10.3198C3.88034 10.6151 3.45552 10.7783 3.0048 10.7783C2.55408 10.7783 2.12927 10.6151 1.81324 10.3198C1.49463 10.0245 1.31849 9.63326 1.31849 9.21317C1.31849 8.79309 1.49463 8.40421 1.81324 8.10895L3.14209 6.87751L2.82866 5.43962L0.878128 7.24478C-0.292709 8.3298 -0.292709 10.099 0.878128 11.184C2.04897 12.2714 3.95805 12.2714 5.13148 11.184Z"
                            fill="#777777" />
                          <path
                            d="M9.80947 5.12015L10.1203 6.56044L12.0708 4.75287C13.2417 3.66786 13.2417 1.8987 12.0708 0.813687C10.8974 -0.273731 8.99092 -0.27133 7.81749 0.813687L5.24009 3.20216C4.67281 3.73027 4.35938 4.42881 4.35938 5.17296C4.35938 5.91711 4.67281 6.61805 5.24009 7.14375C5.52762 7.41021 5.86437 7.61185 6.23738 7.75107L6.41093 7.59024C6.69328 7.32619 6.80207 6.95892 6.72954 6.61805C6.52232 6.54123 6.33063 6.42601 6.17262 6.27958C5.85401 5.98432 5.68045 5.59064 5.68045 5.17296C5.68045 4.75527 5.85401 4.3616 6.17262 4.06874L8.75002 1.67786C9.06604 1.3826 9.49086 1.22177 9.94158 1.22177C10.3923 1.22177 10.8171 1.3826 11.1331 1.67786C11.4517 1.97312 11.6279 2.3668 11.6279 2.78448C11.6279 3.20216 11.4517 3.59344 11.1331 3.8887L9.80947 5.12015Z"
                            fill="#777777" />
                        </g>
                        <defs>
                          <clipPath id="clip0_22718_52857">
                            <rect width="12.9492" height="12" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    }
                    fontSize="15px"
                    fontStyle="normal"
                    fontWeight="400"
                    letterSpacing="-0.3px"
                    textDecorationLine="underline"
                    textDecorationStyle="solid"
                    onClick={async () => {
                      await navigator.clipboard.writeText("https://link.inpock.co.kr/pluscompany");
                      showSuccessToast();
                    }}
                    onContextMenu={async (e) => {
                      e.preventDefault();
                      await navigator.clipboard.writeText("https://link.inpock.co.kr/pluscompany");
                      showSuccessToast();
                    }}
                    _hover={{ bg: "white" }}
                  >
                    덧셈컴퍼니 인포크링크
                  </Button>
                </Box>
                <Image
                  src="/events/refund-verification/refund-verification-4.png"
                  alt="final content"
                  width="100%"
                  height="auto"
                  loading="eager"
                  objectFit="cover"
                />
              </>
            )}
          </VStack>
        </Container>
      </Box>
      <Box>
        <VStack>
          <HStack spacing="18px">
            <Button
              as="a"
              href="https://pluscompany.biz/kakao_service_tax"
              target="_blank" // 새탭
              rel="noopener noreferrer"  // 보안을 위한 속성
              color="#242424"
              fontSize="15px"
              fontStyle="normal"
              fontWeight="700"
              lineHeight="20px"
              w="136px"
              h="71px"
              flexShrink={0}
              borderRadius="20px"
              bg="#F6F7F8"
              letterSpacing="-0.3px"
              textAlign="left"
              _hover={{ bg: "#F6F7F8" }}
            >
              <Flex w="100%" justify="space-between" align="center">
                <Text>
                  세금환급<br />서비스
                </Text>
                <Text>&gt;</Text>
              </Flex>
            </Button>
            <Button
              as="a"
              href="https://pluscompany.biz/kakao_service_addition"
              target="_blank" // 새탭
              rel="noopener noreferrer"  // 보안을 위한 속성
              color="#242424"
              fontSize="15px"
              fontStyle="normal"
              fontWeight="700"
              lineHeight="20px"
              w="136px"
              h="71px"
              flexShrink={0}
              borderRadius="20px"
              bg="#F6F7F8"
              letterSpacing="-0.3px"
              textAlign="left"
              _hover={{ bg: "#F6F7F8" }}
            >
              <Flex w="100%" justify="space-between" align="center">
                <Text>
                  추가환급<br />서비스
                </Text>
                <Text>&gt;</Text>
              </Flex>
            </Button>
          </HStack>
          <Button
            as="a"
            href="https://docs.google.com/forms/d/e/1FAIpQLScf960XfIJlC85z6cidKg590KCgDPNPGMV5Vu64paRBGH8zVQ/viewform"
            target="_blank" // 새탭
            rel="noopener noreferrer"  // 보안을 위한 속성
            m="10px 0 20px 0"
            borderRadius="10px"
            backgroundColor="#32C882"
            width="324px"
            padding="14px 10px"
            display="flex"
            justifyContent="center"
            alignItems="center"
            color="#FFF"
            fontSize="18px"
            fontStyle="normal"
            fontWeight="700"
            lineHeight="25px"
            letterSpacing="-0.36px"
            _hover={{ bg: "#32C882" }}
          >
            이벤트 참여하기
          </Button>
        </VStack>
      </Box>
      <div className="fixed-bottom-container-2">
        {showScrollIndicator && (
          <a className="scroll-indicator">
            <span></span>
          </a>
        )}
      </div>
    </>
  );
}

export default RefundVerification;
